import React from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Icon, Checkbox } from 'semantic-ui-react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { useDashboardPreferences } from '../../contexts/DashboardPreferencesContext';
import { CARD_LABELS } from '../../scenes/Dashboard/Dashboard';
import {
  canAccessRoute, isChapeaExperience, showChapeaPageDropdown, ROUTES,
} from '../../helpers/accessControl';

import './Subheader.css';

const testEnvOptions = {
  CHAPEA: [
    {
      text: 'Convert Files', value: 'CONVERT_FILES', as: Link, to: '/convert-files',
    },
    {
      text: 'IOT Download', value: 'IOT_DOWNLOAD', as: Link, to: '/iot-download',
    },
  ],
  FULL: [
    { text: 'NBL', value: 'NBL' },
    { text: 'ARGOS', value: 'ARGOS' },
    { text: 'FIELD TEST', value: 'FIELD TEST' },
  ],
};

const downloadOptions = [
  {
    text: 'Query', value: 'query', as: Link, to: '/query',
  },
  {
    text: 'Calendar', value: 'calendar', as: Link, to: '/calendar',
  },
  {
    text: 'Dev Download', value: 'dev-download', as: Link, to: '/dev-download',
  },
];

function Subheader({ defaultRoute, allowedEnvironments, allowedRoutes }) {
  const { pathname } = useLocation();
  const history = useHistory();
  const dashboardPrefs = useDashboardPreferences();
  const isDashboard = pathname === '/dashboard';

  let subNavbarDescription;
  switch (pathname) {
    case '/convert-files':
      subNavbarDescription = 'Convert Files';
      break;
    case '/iot-download':
      subNavbarDescription = 'IOT Download';
      break;
    case '/hardware':
      subNavbarDescription = 'Hardware';
      break;
    default:
      subNavbarDescription = 'Testing Environments';
      break;
  }

  // Key the CHAPEA navigation off the user's route access, not the build-time
  // `config.mode`, so CHAPEA users get the CHAPEA page-links in every environment
  // (including PRODUCTION builds where `config.mode` is not 'CHAPEA').
  const chapeaExperience = isChapeaExperience(allowedRoutes);
  // The page-links dropdown (Convert Files / IOT Download) is shown to CHAPEA users
  // everywhere, and to all-access ADMIN users while they are on a CHAPEA page route so
  // they can switch between the two pages via the same upper-right dropdown.
  const pageDropdown = showChapeaPageDropdown(allowedRoutes, pathname);
  const baseEnvOptions = pageDropdown ? testEnvOptions.CHAPEA : testEnvOptions.FULL;
  // For the page-links dropdown the options carry a `to` route (not a testing
  // environment), so gate them by route access instead of the environment list —
  // a CHAPEA user has no NBL/ARGOS/FIELD TEST environments, and filtering those
  // page-links through `allowedEnvironments` would wrongly hide them all.
  const filterEnvOptions = () => {
    if (pageDropdown) {
      return baseEnvOptions.filter((opt) => canAccessRoute(allowedRoutes, opt.to));
    }
    if (allowedEnvironments) {
      return baseEnvOptions.filter((opt) => allowedEnvironments.includes(opt.value));
    }
    return baseEnvOptions;
  };
  const envOptions = filterEnvOptions();

  // CHAPEA users only ever land on /convert-files, /iot-download (and /hardware),
  // so the page-links dropdown must stay visible on those routes — otherwise they
  // have no way to navigate between their pages. Only hide the sub-navbar sections
  // on those routes for the full (non-CHAPEA) experience.
  const hideOnPageRoute = !chapeaExperience
    && (pathname === '/convert-files' || pathname === '/hardware' || pathname === '/iot-download');

  const handleEnvChange = (event, data) => {
    // Page-dropdown items are page-links (rendered as <Link>) and navigate on their
    // own; don't hijack them with environment side-effects. This covers CHAPEA users
    // and ADMIN users switching between the CHAPEA pages.
    if (pageDropdown) {
      const selected = envOptions.find((opt) => opt.value === data.value);
      if (selected?.to && selected.to !== pathname) {
        history.push(selected.to);
      }
      return;
    }
    if (dashboardPrefs) {
      dashboardPrefs.setEnvironment(data.value);
      if (!isDashboard) {
        history.push(defaultRoute || '/dashboard');
      }
    }
  };

  const chapeaDropdownValue = envOptions.find((opt) => opt.to === pathname)?.value
    ?? envOptions[0]?.value;

  // Filter the Download dropdown items by the user's route access so a link only
  // appears when the user can actually reach it. Removing /query from a role
  // (e.g. FOD) must hide the Query item here, not just block the route.
  const visibleDownloadOptions = downloadOptions
    .filter((opt) => canAccessRoute(allowedRoutes, opt.to));

  return (
    <div className="sub-navbar">
      <div className="sub-navbar-top">
        <span className="sub-navbar-description">{subNavbarDescription}</span>
        {pageDropdown ? (
          <div className="testing-environment-dropdown chapea-page-dropdown">
            <Dropdown
              value={chapeaDropdownValue}
              options={envOptions}
              selection
              onChange={handleEnvChange}
              closeOnEscape
              id="testing-dropdown-component"
            />
          </div>
        ) : null}
        { (pathname === '/convert-files' || pathname === '/hardware' || pathname === '/iot-download') ? null : (
          <div className="sub-navbar-connection-indicator indicator-live">
            <div className="sub-navbar-connection-indicator-icon">
              <Icon name="circle" size="tiny" id="indicator-icon" />
              <span className="sub-navbar-connection-indicator-text">Live</span>
            </div>
          </div>
        )}
      </div>
      { hideOnPageRoute || chapeaExperience ? null : (
        <div className="sub-navbar-bottom">
          <div className="sub-navbar-dropdowns">
            <div className="testing-environment-dropdown">
              <Dropdown
                value={dashboardPrefs?.environment ?? envOptions[0]?.value}
                options={envOptions}
                selection
                onChange={handleEnvChange}
                closeOnEscape
                id="testing-dropdown-component"
              />
            </div>
            {isDashboard && dashboardPrefs && (
              <>
                <div className="dashboard-card-selector">
                  {dashboardPrefs.availableCards.map((cardId) => (
                    <Checkbox
                      key={cardId}
                      label={CARD_LABELS[cardId]}
                      checked={dashboardPrefs.selectedCards.includes(cardId)}
                      onChange={() => dashboardPrefs.toggleCard(cardId)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          {!chapeaExperience ? (
            <div className="sub-navbar-controls">
              {visibleDownloadOptions.length > 0 ? (
                <Dropdown
                  className="sub-navbar-control download-dropdown"
                  options={visibleDownloadOptions}
                  trigger={(
                    <span className="sub-navbar-control-icon-group">
                      <Icon name="download" className="sub-navbar-control-icon" />
                      <span className="sub-navbar-control-text">Download</span>
                    </span>
                  )}
                  closeOnEscape
                  selectOnNavigation
                />
              ) : null}
              {canAccessRoute(allowedRoutes, ROUTES.UPLOAD) ? (
                <Link className="sub-navbar-control" to="/upload">
                  <span className="sub-navbar-control-icon-group">
                    <Icon name="upload" className="sub-navbar-control-icon" />
                    <span className="sub-navbar-control-text">Upload</span>
                  </span>
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

Subheader.propTypes = {
  defaultRoute: PropTypes.string,
  allowedEnvironments: PropTypes.arrayOf(PropTypes.string),
  allowedRoutes: PropTypes.arrayOf(PropTypes.string),
};

Subheader.defaultProps = {
  defaultRoute: '/dashboard',
  allowedEnvironments: null,
  allowedRoutes: [],
};

export default Subheader;
